// Admin User Management Types
export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber?: string;
  authType: "manual" | "google" | "admin";
  isActive: boolean;
  isEmailVerified: boolean;
  createdAt: string;
  lastLoginAt?: string;
  roles: string[]; // Changed from AdminUserRole[] to string[]
  profilePicture?: string;
  country?: string;
  city?: string;
  totalOrders?: number;
  totalSpent?: number;
  lastOrderDate?: string;
}

export interface AdminUserRole {
  id: string;
  name: string;
  assignedAt: string;
}

export interface AdminUserDetail extends AdminUser {
  username?: string;
  preferences?: {
    language: string;
    currency?: string;
    notifications?: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
  };
  // Account Lockout
  isLocked?: boolean;
  lockoutEnd?: string;
  failedLoginAttempts?: number;
  // Session Info
  activeSessionCount?: number;
  lastLoginIp?: string;
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  password?: string;
  authType: "manual" | "google" | "admin";
  isActive: boolean;
  isEmailVerified: boolean;
  roleIds?: string[];
  country?: string;
  city?: string;
  profilePicture?: string;
}

export interface UpdateUserRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  roleIds?: string[];
  country?: string;
  city?: string;
  profilePicture?: string;
}

export interface UserFilters {
  search?: string;
  role?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
  authType?: "manual" | "google" | "admin";
  createdAfter?: string;
  createdBefore?: string;
  lastLoginAfter?: string;
  lastLoginBefore?: string;
  country?: string;
  city?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface PaginatedUsersResponse {
  users: AdminUser[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  verifiedUsers: number;
  googleUsers: number;
  localUsers: number;
  recentUsers: number;
  inactiveUsers: number;
  unverifiedUsers: number;
}

// Pricing Types
export interface CategoryPricing {
  id: string;
  categoryName: string;
  pricingType: string;
  fixedPrice?: number;
  pricePerKg?: number;
  pricePerSize?: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  zincKeywords?: string;
  isDefault: boolean;
  basePrice?: number;
}

export interface SpecificProductPricing {
  id: string;
  productKeyword: string;
  categoryId: string;
  categoryName: string;
  fixedPrice: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface PricingStatistics {
  totalCategoryPricing: number;
  activeCategoryPricing: number;
  totalProductPricing: number;
  activeProductPricing: number;
  fixedPricingCount: number;
  perKgPricingCount: number;
  perSizePricingCount: number;
}

export interface ProductTypeKeywords {
  id: number;
  type: string;
  keywords: string;
}

export interface UpdateProductTypeKeywordsRequest {
  keywords: string;
}

export interface CreateCategoryPricingRequest {
  categoryName: string;
  pricingType: string;
  fixedPrice?: number;
  pricePerKg?: number;
  pricePerSize?: number;
  description?: string;
  isActive: boolean;
  zincKeywords?: string;
  isDefault: boolean;
  basePrice?: number;
}

export interface UpdateCategoryPricingRequest {
  categoryName: string;
  pricingType: string;
  fixedPrice?: number;
  pricePerKg?: number;
  pricePerSize?: number;
  description?: string;
  isActive: boolean;
  zincKeywords?: string;
  isDefault: boolean;
  basePrice?: number;
}

export interface CreateProductPricingRequest {
  productKeyword: string;
  categoryId: string;
  fixedPrice: number;
  description?: string;
  isActive: boolean;
}

export interface UpdateProductPricingRequest {
  productKeyword: string;
  categoryId: string;
  fixedPrice: number;
  description?: string;
  isActive: boolean;
}

// Tax and Fee Settings
export interface TaxAndFeeSetting {
  id: string;
  tax: number | null;
  fees: number | null;
  min: number;
  max: number;
  isActive: boolean;
  comment: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateTaxAndFeeRequest {
  tax: number | null;
  fees: number | null;
  min: number;
  max: number;
  isActive: boolean;
  comment: string;
}

export interface UpdateTaxAndFeeRequest {
  tax: number | null;
  fees: number | null;
  min: number;
  max: number;
  isActive: boolean;
  comment: string;
}

export interface ExchangeRate {
  id: string;
  sellValue: number; // USD to TJS conversion rate
  buyValue: number; // TJS to USD conversion rate (optional, for reference)
  isActive: boolean;
  comment: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateExchangeRateRequest {
  sellValue: number;
  buyValue: number;
  isActive: boolean;
  comment: string;
}

export interface UpdateExchangeRateRequest {
  sellValue: number;
  buyValue: number;
  isActive: boolean;
  comment: string;
}

// Promotion Code Types
export enum DiscountType {
  Percentage = 1,
  FixedAmount = 2,
}

export enum FeeType {
  ServiceFee = 1,
  CargoFee = 2,
  AllFees = 3,
  Tax = 4,
  TaxAndFees = 5,
}

export enum CodeType {
  FirstTime = 1,
  Seasonal = 2,
  Loyalty = 3,
  Referral = 4,
  General = 5,
}

export interface ValidatePromotionCodeRequest {
  code: string;
  orderValue: number;
  fees: number;
  cargoFee: number;
  route?: string;
}

export interface PromotionCode {
  id: string;
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  feeType: FeeType;
  codeType: CodeType;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  maxTotalUses?: number;
  maxUsesPerUser?: number;
  currentTotalUses: number;
  minOrderValue?: number;
  routeRestrictions?: string;
  createdBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  isExpired: boolean;
  remainingUses: number;
}

export interface PromotionCodeValidationResult {
  isValid: boolean;
  errorMessage: string;
  discountAmount?: number | null;
  code?: PromotionCode | null;
}

export interface PromotionCodeList {
  id: string;
  code: string;
  description: string;
  isActive: boolean;
  totalUses: number;
  uniqueUsers: number;
  totalDiscountGiven: number;
  endDate?: string;
  maxTotalUses?: number;
  remainingUses: number;
  isExpired: boolean;
}

export interface CreatePromotionCodeRequest {
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  feeType: FeeType;
  codeType: CodeType;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  maxTotalUses?: number;
  maxUsesPerUser?: number;
  minOrderValue?: number;
  routeRestrictions?: string;
  notes?: string;
}

export interface UpdatePromotionCodeRequest {
  description?: string;
  discountType?: DiscountType;
  discountValue?: number;
  feeType?: FeeType;
  codeType?: CodeType;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  maxTotalUses?: number;
  maxUsesPerUser?: number;
  minOrderValue?: number;
  routeRestrictions?: string;
  notes?: string;
}

export interface PromotionCodeUsage {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  orderId: string;
  orderNumber: string;
  discountAmountApplied: number;
  usedAt: string;
}

export interface PromotionCodeDetail extends PromotionCode {
  usages: PromotionCodeUsage[];
  analytics?: PromotionCodeAnalytics;
}

export interface PromotionCodeAnalytics {
  codeId: string;
  code: string;
  description: string;
  totalUses: number;
  uniqueUsers: number;
  remainingUses: number;
  totalDiscountGiven: number;
  averageDiscountPerOrder: number;
  totalRevenueFromCodeUsers: number;
  usesToday: number;
  usesThisWeek: number;
  usesThisMonth: number;
  ordersWithCode: number;
  averageOrderValue: number;
  isActive: boolean;
  expiresAt?: string;
  isExpired: boolean;
}

export interface AvailableRole {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

// Admin Password Reset
export interface AdminResetPasswordRequest {
  newPassword: string;
  confirmNewPassword: string;
  sendNotificationEmail?: boolean;
}

// User Sessions
export interface UserSession {
  id: string;
  createdAt: string;
  expiresAt: string;
  createdByIp: string;
  isActive: boolean;
  revokedAt?: string;
}

// Login History
export interface LoginHistoryEntry {
  id: string;
  createdAt: string;
  ipAddress: string;
  expiresAt: string;
  revokedAt?: string;
  reasonRevoked?: string;
  isActive: boolean;
}

export interface PaginatedLoginHistory {
  entries: LoginHistoryEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// User Address (for admin management)
export interface UserAddress {
  id: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region?: string;
  postalCode?: string;
  country: string;
  phoneNumber: string;
  deliveryInstructions?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateUserAddressRequest {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region?: string;
  postalCode?: string;
  country: string;
  phoneNumber: string;
  deliveryInstructions?: string;
  isDefault?: boolean;
}

export type UpdateUserAddressRequest = Partial<CreateUserAddressRequest>;

// Bulk Operations
export interface BulkUserActionRequest {
  userIds: string[];
}

export interface BulkRoleAssignmentRequest {
  userIds: string[];
  roleId: string;
}

export interface BulkUserActionResponse {
  totalRequested: number;
  successCount: number;
  failedCount: number;
  failures: Array<{
    userId: string;
    errorCode: string;
  }>;
}

// Form validation types
export interface UserFormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  password?: string;
  roleIds?: string;
}

// Modal state types
export type UserModalMode = "create" | "edit" | "view";

export interface UserModalState {
  open: boolean;
  mode: UserModalMode;
  user?: AdminUserDetail;
}

// Export filter options
export interface ExportOptions {
  format: "csv" | "excel";
  filters?: UserFilters;
  includeHeaders: boolean;
}

// Dropshipping Order Item Types
export interface OrderItemDetail {
  id: string;
  orderId: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image: string;

  // Dropshipping fields
  retailer?: string;
  itemStatusId?: number;
  itemStatusName?: string;
  itemStatusDisplayName?: string;
  itemStatusColor?: string;
  notes?: string;
  trackingNumber?: string;
  retailerOrderId?: string; // Retailer's order ID (Amazon, Walmart, etc.)
  retailerTrackingId?: string; // Retailer's tracking ID (Amazon, Walmart, etc.)
  flightNumber?: string;
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;

  // Weight and shipping fields
  estimatedWeight?: number; // Estimated weight in kg before sending to cargo company
  cargoWeight?: number; // Actual cargo company weight in kg (set after weighing)
  itemCarrierCargoFee?: number; // Shipping fee for this item
  cargoPrice?: number; // Estimated shipping price for this item

  // Seller info (from offers API)
  sellerName?: string;
  sellerId?: string;
  offerId?: string;
  sellerShippingFee?: number;
  sellerShippingFeeTjs?: number | null;

  // Price in TJS
  priceTjs?: number | null;
  cargoPriceTjs?: number | null;

  // Soft delete
  isDeleted?: boolean;
  deletedAt?: string;

  // Computed fields
  hasTrackingNumber: boolean;
  hasNotes: boolean;
  isShipped: boolean;
  isOrdered: boolean;
}

export interface OrderItemHistoryEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  description: string;
  performedBy?: string;
  createdAt: string;
  note?: string;
  oldValuesJson?: string;
  newValuesJson?: string;
}

export interface PaginatedHistoryResponse {
  items: OrderItemHistoryEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Flat item view with order context (for Items Management page)
export interface OrderItemFlat extends OrderItemDetail {
  orderNumber: string;
  customerName: string;
  customerEmail: string;
}

export interface OrderItemFlatFilters {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  itemStatusId?: number;
  retailer?: string;
  hasRetailerOrderId?: boolean;
  hasRetailerTrackingId?: boolean;
  retailerOrderId?: string;
  retailerTrackingId?: string;
  flightNumber?: string;
  hasFlightNumber?: boolean;
  pickUpLocation?: string;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface PaginatedOrderItemsResponse {
  items: OrderItemFlat[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ItemStatus {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
  createdAt: string;
  updatedAt: string;
  orderItemCount: number;

  // Computed fields
  allowedNextStatusIds: number[];
  isFinalStatus: boolean;
}

// ItemStatus CRUD Types
export interface CreateItemStatusRequest {
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
}

export interface UpdateItemStatusRequest {
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
}

export interface ItemStatusFilters {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface PaginatedItemStatusResponse {
  statuses: ItemStatus[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ItemStatusStatistics {
  totalStatuses: number;
  activeStatuses: number;
  inactiveStatuses: number;
  statusesWithItems: number;
  statusesWithoutItems: number;
  statusUsageCount: Record<string, number>;
}

export interface BulkUpdateItemStatusRequest {
  statusIds: number[];
  isActive: boolean;
}

export interface ReorderItemStatusesRequest {
  statuses: ItemStatusReorderItem[];
}

export interface ItemStatusReorderItem {
  id: number;
  sortOrder: number;
}

export interface ItemStatusValidation {
  isValid: boolean;
  errors: string[];
}

export interface ItemStatusTransitionValidation {
  canTransition: boolean;
  reason: string;
  requiredFields: string[];
}

// Modal state types for ItemStatus
export type ItemStatusModalMode = "create" | "edit" | "view";

export interface ItemStatusModalState {
  open: boolean;
  mode: ItemStatusModalMode;
  status?: ItemStatus;
}

export interface UpdateOrderItemRequest {
  orderItemId: string;
  retailer?: string;
  itemStatusId?: number;
  notes?: string;
  trackingNumber?: string;
  retailerOrderId?: string; // Retailer's order ID (Amazon, Walmart, etc.)
  retailerTrackingId?: string; // Retailer's tracking ID (Amazon, Walmart, etc.)
  flightNumber?: string;
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;
  price?: number;
  quantity?: number;
  cargoWeight?: number; // Actual cargo company weight in kg (editable)
  itemCarrierCargoFee?: number; // Shipping fee for this item (editable)
}

export interface MarkAsShippedRequest {
  trackingNumber: string;
  notes?: string;
}

export interface AddOrderItemRequest {
  orderId: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image: string;
  retailer?: string;
  cargoPrice?: number;
  estimatedWeight?: number;
  notes?: string;
}

export interface BulkUpdateItemFieldsRequest {
  orderItemIds: string[];
  retailerOrderId?: string;
  retailerTrackingId?: string;
  flightNumber?: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
  retailer?: string;
  cargoWeight?: number;
  itemCarrierCargoFee?: number;
  notes?: string;
}

// Order Status Management Types
export interface OrderStatus {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
  createdAt: string;
  updatedAt: string;
  orderCount: number;
}

export interface OrderStatusFilters {
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface PaginatedOrderStatusResponse {
  statuses: OrderStatus[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface OrderStatusStatistics {
  totalStatuses: number;
  activeStatuses: number;
  inactiveStatuses: number;
  statusesInUse: number;
  mostUsedStatus: string;
  leastUsedStatus: string;
}

export interface CreateOrderStatusRequest {
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
}

export interface UpdateOrderStatusRequest {
  name?: string;
  displayName?: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
  canTransitionTo?: number[];
  requiresTrackingNumber?: boolean;
  requiresNote?: boolean;
}

export interface BulkUpdateOrderStatusRequest {
  statusIds: number[];
  isActive: boolean;
}

export interface ReorderStatusesRequest {
  statuses: StatusReorderItem[];
}

export interface StatusReorderItem {
  id: number;
  sortOrder: number;
}

export interface StatusValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface StatusTransitionValidation {
  isValid: boolean;
  canTransition: boolean;
  reason?: string;
}

// Form validation types
export interface OrderStatusFormErrors {
  name?: string;
  displayName?: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: string;
  canTransitionTo?: string;
}

// Modal state types
export type OrderStatusModalMode = "create" | "edit" | "view";

export interface OrderStatusModalState {
  open: boolean;
  mode: OrderStatusModalMode;
  status?: OrderStatus;
}

// OrderItemStatus CRUD Types
export interface OrderItemStatus {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  color: string;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
  createdAt: string;
  updatedAt: string;
  orderItemCount: number;

  // Computed fields
  allowedNextStatusIds: number[];
  isFinalStatus: boolean;
}

// Payment Method Discount Types
export enum PaymentMethodType {
  Stripe = 1,
  Alif = 2,
  DcCard = 3,
  PayPal = 4,
  BankTransfer = 5,
}

export interface PaymentMethodDiscount {
  id: string;
  paymentMethodType: PaymentMethodType;
  paymentMethodTypeDisplay: string;
  discountType: DiscountType;
  discountValue: number;
  feeType: FeeType;
  feeTypeDisplay: string;
  description: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  minOrderValue?: number;
  maxTotalUses?: number;
  currentTotalUses: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  notes?: string;
}

export interface CreatePaymentMethodDiscountRequest {
  paymentMethodType: PaymentMethodType;
  discountType: DiscountType;
  discountValue: number;
  feeType: FeeType;
  description: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  minOrderValue?: number;
  maxTotalUses?: number;
  notes?: string;
}

export interface UpdatePaymentMethodDiscountRequest {
  paymentMethodType?: PaymentMethodType;
  discountType?: DiscountType;
  discountValue?: number;
  feeType?: FeeType;
  description?: string;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  minOrderValue?: number;
  maxTotalUses?: number;
  notes?: string;
}
